import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BookOpen,
  ChevronRight,
  Clock,
  Crown,
  ShieldX,
  Trophy,
} from "lucide-react";
import axios from "../../../../axios";
import { errorToast } from "../../../../utils/toast";
import { useUser } from "../../../../hooks/use-user";
import Loding from "../../../../componant/Loader/Loding";
import coinIcon from "../../../../Images/L2E_single_coin.png";
import { getContestReturnUrl } from "./contestNavigation";

const parseBoolean = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  String(value).toLowerCase() === "true";

const WinnersPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const returnUrl = location.state?.returnUrl || getContestReturnUrl();
  const contestIdFromState = location.state?.contestId;
  const { data: user } = useUser();

  const [countData, setCountData] = useState(null);
  const [reportsData, setReportsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [hasContestEnded, setHasContestEnded] = useState(false);


  
  useEffect(() => {
    const fetchData = async () => {
      if (!contestIdFromState) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const [detailsRes, questionsRes, reportRes, contestsRes] =
          await Promise.all([
            axios.get("contestDetails", {
              params: { contestId: contestIdFromState },
            }),

            axios.get(
              `getContestQuestionsByContestId/${contestIdFromState}`,
            ),

            axios.get("getContestReport", {
              params: { contestId: contestIdFromState },
            }),

            axios
              .get("getAllContestForUser")
              .catch(() => ({ data: { result: [] } })),
          ]);

        const details = detailsRes?.data?.result;
        const contests = contestsRes?.data?.result || [];
        const selectedContest = Array.isArray(contests)
          ? contests.find(
              (contest) =>
                String(contest?._id || contest?.contestId || contest?.id) ===
                  String(contestIdFromState) ||
                String(contest?.contestId) === String(details?.UniqueContestId),
            )
          : null;
        const showAnswerKey =
          parseBoolean(selectedContest?.showAnswerKey) 
      
        if (details) {
          setHasContestEnded(details.hasContestEnded || false);

          setCountData({
            rightCount: details.totalRightAnswerCount || 0,
            wrongCount: details.totalWrongAnswerCount || 0,
            totalAttempts: details.totalAttempts || 0,
            accuracy: details.accuracy || 0,
            totalTimeTaken: details.totalTimeTaken || 0,
            name: details.name,
            contestId: details.contestId,
            UniqueContestId: details.UniqueContestId,
            contestName:
              details.contestName || selectedContest?.contestName,
            showAnswerKey,
            totalContestSlot: details.totalContestSlot,
            userRank: details.hasContestEnded
              ? details.userRank
              : null,
            winningAmount: details.hasContestEnded
              ? details.winningAmount
              : 0,
            totalUserContestJoinedCount:
              details.totalUserContestJoinedCount,
          });
        }

        const questions =
          questionsRes?.data?.result?.questions;

        if (Array.isArray(questions)) {
          setTotalQuestions(questions.length);
        }

        const report = reportRes?.data?.result;

        if (Array.isArray(report)) {
          setReportsData(report);
        }
      } catch (err) {
        errorToast(
          err?.response?.data?.message ||
            "Failed to load data",
        );
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user, contestIdFromState, location.state?.showAnswerKey]);

  const rightCount = countData?.rightCount || 0;
  const wrongCount = countData?.wrongCount || 0;

  const totalAttempts =
    countData?.totalAttempts ||
    rightCount + wrongCount;

  const skippedCount = Math.max(
    totalQuestions - totalAttempts,
    0,
  );

  const accuracy = countData?.accuracy || 0;

  const winningAmount =
    countData?.winningAmount || 0;
  const winningAmountValue = Number(winningAmount || 0);

  const userRank = countData?.userRank;

  const score = useMemo(
    () =>
      Math.max(
        rightCount - wrongCount * 0.25,
        0,
      ),
    [rightCount, wrongCount],
  );

  const isRejected =
    hasContestEnded && winningAmountValue <= 0;

  const formatScore = (value) => {
    const rounded = Number(value || 0).toFixed(2);

    return rounded.endsWith(".00")
      ? String(Math.round(Number(rounded)))
      : rounded.replace(/0$/, "");
  };

  const formatTime = (seconds) => {
    if (!seconds) return "0s";

    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    return mins > 0
      ? `${mins}m ${secs}s`
      : `${secs}s`;
  };

  const getPlayerName = (entry) =>
    entry?.user?.name ||
    entry?.name ||
    "Rajesh Patil";

  const getPlayerScore = (entry) =>
    entry?.score ??
    entry?.contestScore ??
    entry?.rightCount ??
    0;

  const getPlayerPrize = (entry) =>
    entry?.prizeWon ??
    entry?.winningAmount ??
    entry?.coins ??
    0;

  const displayName = countData?.name || user?.name || "User";
  const displayContestId = countData?.UniqueContestId || contestIdFromState;
  const displayRank = userRank || "--";
  const shouldShowAnswerKey = Boolean(countData?.showAnswerKey);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#EFE7F5]">
        <Loding />
      </div>
    );
  }

 return (
  <div className={`min-h-screen ${isRejected ? "bg-[#E9DDEB]" : "bg-[#FFF4D8]"}`}>
    <style>
      {`
        @keyframes winners-page-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes winners-page-pulse {
          0%, 100% { opacity: 0.55; }
          50% { opacity: 0.9; }
        }
      `}
    </style>

    <div className="mx-auto min-h-screen max-w-[390px] pb-8 lg:mt-10 lg:max-w-[1180px] lg:px-8 lg:py-8">
      <div className="lg:grid lg:grid-cols-[1fr_420px] lg:items-start lg:gap-8">
        <div className="lg:overflow-hidden lg:rounded-[28px] lg:bg-white lg:shadow-[0_10px_30px_rgba(44,25,60,0.12)]">
          <div
            className={`relative overflow-hidden px-4 pb-5 pt-4 ${
              isRejected ? "bg-[#D8CBDD]" : "bg-[#FFE7A3]"
            } lg:p-7`}
          >
            {userRank === 1 && !isRejected && (
              <div className="pointer-events-none absolute left-1/2 top-[120px] h-[430px] w-[430px] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full opacity-75 lg:hidden md:hidden">
                <div
                  className="relative h-full w-full"
                  style={{
                    animation:
                      "winners-page-spin 7s linear infinite, winners-page-pulse 1.8s ease-in-out infinite",
                  }}
                >
                  {Array.from({ length: 16 }).map((_, index) => (
                    <span
                      key={index}
                      className="absolute left-1/2 top-1/2 h-8 w-[230px] origin-left rounded-full bg-white/70"
                      style={{ transform: `rotate(${index * 22.5}deg)` }}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="relative z-10 mt-5 lg:flex lg:items-center lg:gap-6">
              <div className="flex justify-center lg:w-[170px] lg:shrink-0">
                <div className="relative flex w-40 flex-col items-center">
                  <div className="flex h-32 w-32 items-center justify-center rounded-full bg-gradient-to-br from-[#A64EE0] to-bgBlue shadow-[0_18px_42px_rgba(55,21,69,0.38)]">
                    <div className="flex h-16 w-16 items-center justify-center text-[#FDB21A]">
                      {isRejected ? (
                        <ShieldX size={44} />
                      ) : userRank === 1 ? (
                        <Crown size={46} fill="currentColor" />
                      ) : (
                        <Trophy size={46} fill="currentColor" />
                      )}
                    </div>
                  </div>

                  <div className="-mt-4 rounded-xl bg-[#FDA51E] px-8 py-2 text-[13px] font-black uppercase text-[#6B3E00] shadow-lg">
                    Rank {displayRank}
                  </div>
                </div>
              </div>

              <div className="mt-8 flex items-end gap-3 lg:mt-0 lg:flex-1 lg:items-center">
                <img
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                    displayName
                  )}&background=562566&color=fff&size=128`}
                  alt="avatar"
                  className="h-16 w-16 rounded-full border-4 border-[#FFB21A] object-cover lg:h-20 lg:w-20"
                />

                <div className="min-w-0 flex-1">
                  <h2 className="truncate text-base font-extrabold text-[#2E2535] lg:text-2xl">
                    {displayName}
                  </h2>

                  <p className="text-[10px] font-medium text-[#6E6472] lg:text-sm">
                    Contest ID: {displayContestId}
                  </p>

                  <div className="mt-2 rounded-md bg-bgBlue px-3 py-2 text-center text-xs font-bold text-[#FFD052] lg:max-w-[420px] lg:text-sm">
                    {isRejected ? "Better Luck Next Time" : "Congratulations Winner"}
                  </div>
                </div>
              </div>
            </div>

            <div className="relative z-10 mt-4 grid grid-cols-4 gap-2 lg:mt-8 lg:gap-4">
              {[
                ["Total Questions", totalQuestions],
                ["Correct Answer", rightCount],
                ["Incorrect Answer", wrongCount],
                ["Attempted Questions", totalAttempts],
              ].map(([label, value], index) => (
                <div
                  key={label}
                  className="rounded-md bg-[#F5A623] px-1 py-2 text-center lg:rounded-2xl lg:px-3 lg:py-3"
                >
                  <p className="text-[9px] font-bold text-[#6B3E00] lg:text-sm">
                    {label}
                  </p>

                  <p
                    className={`mt-1 text-sm font-black lg:text-3xl ${
                      index === 1
                        ? "text-green-600"
                        : index === 2
                        ? "text-red-600"
                        : "text-bgBlue"
                    }`}
                  >
                    {formatScore(value).padStart(2, "0")}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="px-4 lg:px-7 lg:pb-7 lg:pt-2">
            <div className="mt-1 grid grid-cols-2 overflow-hidden rounded-xl bg-bgBlue text-center text-white shadow-lg lg:mt-0 lg:rounded-3xl">
              <div className="py-3 lg:py-2">
                <p className="text-2xl font-black lg:text-4xl">
                  {formatScore(score)}
                </p>

                <p className="mt-1 text-[10px] font-bold uppercase lg:text-sm">
                  {isRejected ? "Avg Score" : "Score"}
                </p>
              </div>

              <div className="py-4 lg:py-2">
                <p className="text-2xl font-black lg:text-4xl">
                  {isRejected ? formatScore(accuracy) : `${formatScore(accuracy)}%`}
                </p>

                <p className="mt-1 text-[10px] font-bold uppercase lg:text-sm">
                  Accuracy
                </p>
              </div>
            </div>

            {shouldShowAnswerKey && (
              <button
                type="button"
                onClick={() =>
                  navigate("/dash/contest-answer-key", {
                    state: {
                      contestId: contestIdFromState,
                      contestName: countData?.contestName,
                      returnUrl,
                    },
                  })
                }
                className="mt-5 flex w-full items-center gap-4 rounded-2xl bg-gradient-to-r from-[#562566] to-[#8733C4] px-5 py-4 text-left text-white shadow-[0_10px_24px_rgba(86,37,102,0.28)]"
              >
                <BookOpen className="shrink-0" size={28} />
                <span className="min-w-0 flex-1">
                  <span className="block text-lg font-extrabold">
                    Answer Key
                  </span>
                  <span className="mt-1 block text-sm text-white/80">
                    Review selected and correct answers
                  </span>
                </span>
                <ChevronRight className="shrink-0" size={28} />
              </button>
            )}
          </div>
        </div>

        <div className="px-4 lg:px-0">
          <div className="mt-4 rounded-md bg-[#F5A623] py-2 text-center text-sm font-bold text-white lg:mt-0 lg:rounded-t-[28px] lg:py-4 lg:text-lg">
            Top Ranks
          </div>

          <div className="mt-2 space-y-2 lg:mt-0 lg:rounded-b-[28px] lg:bg-white lg:p-5 lg:shadow-[0_10px_30px_rgba(44,25,60,0.12)]">
            {(reportsData.length ? reportsData : [])
              .slice(0, 10)
              .map((entry, index) => {
                const rank = index + 1;
                const isCurrentUser =
                  entry?.user?._id === user?._id || entry?._id === user?._id;

                return (
                  <div
                    key={entry?._id || index}
                    className={`flex items-center gap-3 rounded-lg px-2 py-2 shadow-sm lg:rounded-2xl lg:px-4 lg:py-3 ${
                      rank <= 3 || isCurrentUser
                        ? "bg-bgBlue"
                        : "bg-white lg:bg-[#F7F3FA]"
                    }`}
                  >
                    <img
                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                        getPlayerName(entry)
                      )}&background=F5A623&color=fff&size=96`}
                      alt=""
                      className="h-10 w-10 rounded-full border-2 border-[#F5A623] lg:h-12 lg:w-12"
                    />

                    <div className="min-w-0 flex-1">
                      <p
                        className={`truncate text-sm font-extrabold lg:text-base ${
                          rank <= 3 || isCurrentUser
                            ? "text-white"
                            : "text-[#2F2935]"
                        }`}
                      >
                        {isCurrentUser ? "You" : getPlayerName(entry)}
                      </p>

                      <p
                        className={`text-[10px] lg:text-xs ${
                          rank <= 3 || isCurrentUser
                            ? "text-white/70"
                            : "text-gray-500"
                        }`}
                      >
                        Contest Score: {formatScore(getPlayerScore(entry))}
                      </p>
                    </div>

                    <div className="flex items-center gap-1">
                      <span className="flex h-6 min-w-8 items-center justify-center rounded-full bg-[#F5A623] px-2 text-[10px] font-black text-white lg:h-7 lg:px-3">
                        #{rank}
                      </span>

                      <span className="inline-flex items-center gap-1 rounded-full bg-[#F5A623] px-2 py-1 text-[9px] font-black text-bgBlue lg:px-3 lg:text-xs">
                        <img src={coinIcon} alt="" className="h-4 w-4 object-contain" />
                        {getPlayerPrize(entry)}
                      </span>
                    </div>
                  </div>
                );
              })}

            {!reportsData.length && (
              <div className="rounded-lg bg-white px-4 py-8 text-center text-sm text-gray-500 lg:bg-[#F7F3FA]">
                Rankings are being calculated. Please check again shortly.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-2 px-4 text-center text-xs text-bgBlue lg:grid-cols-3 lg:gap-5 lg:px-0">
        <div className="rounded-lg bg-white p-3 lg:rounded-2xl lg:p-5 lg:shadow-[0_10px_30px_rgba(44,25,60,0.08)]">
          <Clock className="mx-auto mb-1" size={16} />
          {formatTime(countData?.totalTimeTaken)}
        </div>

        <div className="rounded-lg bg-white p-3 lg:rounded-2xl lg:p-5 lg:shadow-[0_10px_30px_rgba(44,25,60,0.08)]">
          <Trophy className="mx-auto mb-1" size={16} />
          Rank {displayRank}
        </div>

        <div className="rounded-lg bg-white p-3 lg:rounded-2xl lg:p-5 lg:shadow-[0_10px_30px_rgba(44,25,60,0.08)]">
          <span className="block font-black lg:text-xl">{skippedCount}</span>
          Skipped
        </div>
      </div>

      {!hasContestEnded && (
        <p className="mx-4 mt-4 rounded-lg bg-white px-4 py-3 text-center text-sm font-semibold text-bgBlue lg:mx-0">
          Final rank and rewards will be available after the contest ends.
        </p>
      )}
    </div>
  </div>
);
};

export default WinnersPage;
